import api from './api';

/**
 * Navigation Module interface
 * 
 * Represents a navigation module in the TMOS system.
 * Modules are the top-level navigation elements.
 */
export interface NavigationModule {
  /** Unique module identifier */
  id: string;
  /** Module code for programmatic access */
  module_code: string;
  /** Human-readable module name */
  module_name: string;
  /** Optional module description */
  description?: string;
  /** Display order for sorting */
  display_order: number;
  /** Whether the module is active */
  is_active: boolean;
  /** Creation timestamp */
  created_at: string;
  /** Last update timestamp */
  updated_at: string;
}

/**
 * Navigation Screen interface
 * 
 * Represents a navigation screen within a module.
 * Screens are the second-level navigation elements.
 */
export interface NavigationScreen {
  /** Unique screen identifier */
  id: string;
  /** ID of the parent module */
  module_id: string;
  /** Screen code for programmatic access */
  screen_code: string;
  /** Human-readable screen name */
  screen_name: string;
  /** Optional screen description */
  description?: string;
  /** Frontend route path */
  route_path: string;
  /** Display order for sorting */
  display_order: number;
  /** Whether the screen is active */
  is_active: boolean;
  /** Creation timestamp */
  created_at: string;
  /** Last update timestamp */
  updated_at: string;
  /** Optional module name for display */
  module_name?: string;
}

/**
 * Navigation Node interface
 * 
 * Represents a navigation node within a screen.
 * Nodes are the third-level navigation elements and represent actions.
 */
export interface NavigationNode {
  /** Unique node identifier */
  id: string;
  /** ID of the parent screen */
  screen_id: string;
  /** Node code for programmatic access */
  node_code: string;
  /** Human-readable node name */
  node_name: string;
  /** Optional node description */
  description?: string;
  /** Type of action this node represents */
  action_type: string;
  /** Display order for sorting */
  display_order: number;
  /** Whether the node is active */
  is_active: boolean;
  /** Creation timestamp */
  created_at: string;
  /** Last update timestamp */
  updated_at: string;
  /** Optional screen name for display */
  screen_name?: string;
}

/**
 * Navigation Hierarchy interface
 * 
 * Represents the complete navigation hierarchy including modules, screens, and nodes.
 * This is used for displaying the full navigation structure in the UI.
 */
export interface NavigationHierarchy {
  /** Unique hierarchy identifier */
  id: string;
  /** Hierarchy code for programmatic access */
  code: string;
  /** Human-readable hierarchy name */
  name: string;
  /** Optional hierarchy description */
  description?: string;
  /** Display order for sorting */
  display_order: number;
  /** Whether the hierarchy is active */
  is_active: boolean;
  /** Nested screens within this hierarchy */
  screens: {
    [key: string]: {
      /** Screen identifier */
      id: string;
      /** Screen code */
      code: string;
      /** Screen name */
      name: string;
      /** Optional screen description */
      description?: string;
      /** Frontend route path */
      route_path: string;
      /** Display order */
      display_order: number;
      /** Whether screen is active */
      is_active: boolean;
      /** Navigation location (SIDEBAR, TOP, BOTH) */
      location?: string;
      /** Nested nodes within this screen */
      nodes: {
        [key: string]: {
          /** Node identifier */
          id: string;
          /** Node code */
          code: string;
          /** Node name */
          name: string;
          /** Optional node description */
          description?: string;
          /** Action type */
          action_type: string;
          /** Display order */
          display_order: number;
          /** Whether node is active */
          is_active: boolean;
        };
      };
    };
  };
}

/**
 * Navigation Management Service
 * 
 * Provides API access to navigation management operations including modules,
 * screens, nodes, and hierarchy management.
 * 
 * @example
 * ```typescript
 * const navService = new NavigationManagementService();
 * const modules = await navService.getModules();
 * ```
 */
class NavigationManagementService {
  // ============================================================================
  // MODULE MANAGEMENT
  // ============================================================================

  /**
   * Get all navigation modules
   * 
   * Retrieves all navigation modules from the backend.
   * 
   * @returns Promise<NavigationModule[]> - Array of navigation modules
   */
  async getModules(): Promise<NavigationModule[]> {
    const response = await api.get('/navigation/modules');
    return response.data.data;
  }

  /**
   * Create a new navigation module
   * 
   * @param module - Module data to create (excluding id, created_at, updated_at)
   * @returns Promise<NavigationModule> - Created module
   */
  async createModule(module: Omit<NavigationModule, 'id' | 'created_at' | 'updated_at'>): Promise<NavigationModule> {
    const response = await api.post('/navigation/modules', module);
    return response.data.data;
  }

  /**
   * Update an existing navigation module
   * 
   * @param id - Module ID to update
   * @param module - Partial module data to update
   * @returns Promise<NavigationModule> - Updated module
   */
  async updateModule(id: string, module: Partial<NavigationModule>): Promise<NavigationModule> {
    const response = await api.put(`/navigation/modules/${id}`, module);
    return response.data.data;
  }

  /**
   * Delete a navigation module
   * 
   * @param id - Module ID to delete
   */
  async deleteModule(id: string): Promise<void> {
    await api.delete(`/navigation/modules/${id}`);
  }

  /**
   * Deactivate a navigation module (soft delete)
   * 
   * @param id - Module ID to deactivate
   * @returns Promise<NavigationModule> - Deactivated module
   */
  async deactivateModule(id: string): Promise<NavigationModule> {
    const response = await api.patch(`/navigation/modules/${id}/deactivate`);
    return response.data.data;
  }

  // ============================================================================
  // SCREEN MANAGEMENT
  // ============================================================================

  /**
   * Get navigation screens
   * 
   * @param moduleId - Optional module ID to filter screens
   * @returns Promise<NavigationScreen[]> - Array of navigation screens
   */
  async getScreens(moduleId?: string): Promise<NavigationScreen[]> {
    const params = moduleId ? { module_id: moduleId } : {};
    const response = await api.get('/navigation/screens', { params });
    return response.data.data;
  }

  /**
   * Create a new navigation screen
   * 
   * @param screen - Screen data to create (excluding id, created_at, updated_at, module_name)
   * @returns Promise<NavigationScreen> - Created screen
   */
  async createScreen(screen: Omit<NavigationScreen, 'id' | 'created_at' | 'updated_at' | 'module_name'>): Promise<NavigationScreen> {
    const response = await api.post('/navigation/screens', screen);
    return response.data.data;
  }

  /**
   * Update an existing navigation screen
   * 
   * @param id - Screen ID to update
   * @param screen - Partial screen data to update
   * @returns Promise<NavigationScreen> - Updated screen
   */
  async updateScreen(id: string, screen: Partial<NavigationScreen>): Promise<NavigationScreen> {
    const response = await api.put(`/navigation/screens/${id}`, screen);
    return response.data.data;
  }

  /**
   * Delete a navigation screen
   * 
   * @param id - Screen ID to delete
   */
  async deleteScreen(id: string): Promise<void> {
    await api.delete(`/navigation/screens/${id}`);
  }

  /**
   * Deactivate a navigation screen (soft delete)
   * 
   * @param id - Screen ID to deactivate
   * @returns Promise<NavigationScreen> - Deactivated screen
   */
  async deactivateScreen(id: string): Promise<NavigationScreen> {
    const response = await api.patch(`/navigation/screens/${id}/deactivate`);
    return response.data.data;
  }

  /**
   * Update navigation screen location
   * 
   * @param id - Screen ID to update
   * @param location - New location (SIDEBAR, TOP, BOTH)
   * @returns Promise<NavigationScreen> - Updated screen
   */
  async updateScreenLocation(id: string, location: string): Promise<NavigationScreen> {
    const response = await api.patch(`/navigation/screens/${id}/location`, { location });
    return response.data.data;
  }

  // ============================================================================
  // NODE MANAGEMENT
  // ============================================================================

  async getNodes(screenId?: string): Promise<NavigationNode[]> {
    const params = screenId ? { screen_id: screenId } : {};
    const response = await api.get('/navigation/nodes', { params });
    return response.data.data;
  }

  async createNode(node: Omit<NavigationNode, 'id' | 'created_at' | 'updated_at' | 'screen_name'>): Promise<NavigationNode> {
    const response = await api.post('/navigation/nodes', node);
    return response.data.data;
  }

  async updateNode(id: string, node: Partial<NavigationNode>): Promise<NavigationNode> {
    const response = await api.put(`/navigation/nodes/${id}`, node);
    return response.data.data;
  }

  async deleteNode(id: string): Promise<void> {
    await api.delete(`/navigation/nodes/${id}`);
  }

  // ============================================================================
  // HIERARCHY MANAGEMENT
  // ============================================================================

  /**
   * Get the complete navigation hierarchy
   * 
   * Retrieves the full navigation structure including modules, screens, and nodes
   * organized in a hierarchical format for UI display.
   * 
   * @returns Promise<NavigationHierarchy[]> - Complete navigation hierarchy
   */
  async getFullHierarchy(): Promise<NavigationHierarchy[]> {
    const response = await api.get('/navigation/hierarchy');
    return response.data.data;
  }
}

export const navigationManagementService = new NavigationManagementService();
